import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, GraduationCap, MapPin, Landmark, Search, Building2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * SCHOOL DISCOVERY PAGE — LOCKED
 * 
 * Route: /schools
 * 
 * Display:
 * - Name
 * - Location
 * - Type
 * - Commons they steward
 * 
 * Rules:
 * - Read-only
 * - No student visibility
 * - No contact actions
 * - No promotion language
 */

export default function SchoolsDiscovery() {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSchools, setFilteredSchools] = useState([]);

  useEffect(() => {
    const loadSchools = async () => {
      try {
        const response = await axios.get(`${API}/api/discovery/schools`);
        setSchools(response.data.schools || []);
        setFilteredSchools(response.data.schools || []);
      } catch (error) {
        console.error('Failed to load schools:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSchools();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = schools.filter(school => 
        school.name?.toLowerCase().includes(query) ||
        school.location?.toLowerCase().includes(query)
      );
      setFilteredSchools(filtered);
    } else {
      setFilteredSchools(schools);
    }
  }, [searchQuery, schools]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-40"
        style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}
      >
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 rounded-lg"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <GraduationCap className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>
              Schools
            </h1>
          </div>
        </div>
      </header>

      {/* Search */}
      <div className="max-w-3xl mx-auto px-4 py-4">
        <div className="relative">
          <Search 
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" 
            style={{ color: 'var(--fy-text-secondary)' }} 
          />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by school name or location..."
            className="pl-10 rounded-xl"
            style={{ backgroundColor: 'var(--fy-surface-muted)' }}
            data-testid="school-search-input"
          />
        </div>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 pb-8">
        {/* Info Banner */}
        <div 
          className="p-4 rounded-xl mb-6"
          style={{ backgroundColor: 'var(--fy-surface-muted)' }}
        >
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            Schools listed here are verified institutions on LikeKind. This page is read-only to help families verify legitimacy.
          </p>
        </div>

        {/* Schools List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto" 
                 style={{ borderColor: 'var(--fy-text-secondary)', borderTopColor: 'transparent' }} />
          </div>
        ) : filteredSchools.length > 0 ? (
          <div className="space-y-4">
            {filteredSchools.map((school) => (
              <div
                key={school.school_id || school.id}
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                data-testid={`school-${school.school_id || school.id}`}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--fy-surface-muted)' }}
                  >
                    <GraduationCap className="w-6 h-6" style={{ color: 'var(--fy-text-secondary)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 
                      className="font-bold mb-1"
                      style={{ color: 'var(--fy-text-primary)' }}
                    >
                      {school.name}
                    </h3>
                    
                    {school.location && (
                      <div className="flex items-center gap-1 mb-2">
                        <MapPin className="w-3.5 h-3.5" style={{ color: 'var(--fy-text-disabled)' }} />
                        <span className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                          {school.location}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap gap-2">
                      {school.type && (
                        <span 
                          className="text-xs px-2 py-1 rounded-full"
                          style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}
                        >
                          {school.type}
                        </span>
                      )}
                      {school.commons_count > 0 && (
                        <span 
                          className="text-xs px-2 py-1 rounded-full flex items-center gap-1"
                          style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}
                        >
                          <Landmark className="w-3 h-3" />
                          {school.commons_count} Commons
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <GraduationCap className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
            <p style={{ color: 'var(--fy-text-secondary)' }}>
              {searchQuery ? 'No schools match your search' : 'No schools listed yet'}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
